import { AMFTaskCardConfiguration } from 'component/AMFTaskObjectCard'
import { SortOption, YesObject } from '@yes.technology/react-toolkit'
import {
  Array,
  Boolean,
  Dictionary,
  Literal,
  Optional,
  Record,
  String,
  Union,
  Unknown,
  Number
} from 'runtypes'
import { StyleMap } from 'site/Renderer/hooks/useStyleMap'

export type CardModel =
  | 'GenericObjectCard'
  | 'TaskObjectCard'
  | 'AMFTaskObjectCard'
  | 'ContactObjectCard'
  | 'TransportOrderCard'

const CardModelGuard = Union(
  Literal('GenericObjectCard'),
  Literal('TaskObjectCard'),
  Literal('AMFTaskObjectCard'),
  Literal('ContactObjectCard'),
  Literal('TransportOrderCard')
)

const ColumnTypeGuard = Record({
  grouped_by_value: String,
  label: String,
  text_fading: Optional(Boolean)
})

const LayoutTypeGuard = Record({
  column_labels_text_fading: Optional(Boolean),
  order_by: Optional(Array(Dictionary(String, String))),
  sections: Optional(Array(Record({ columns: Array(ColumnTypeGuard) })))
})

const AssociatedUsersRelationTypeGuard = Record({
  relation: String,
  readableName: Optional(String),
  image_path: Optional(String)
})

export type ColumnProps = {
  grouped_by_value: string
  label: string
  text_fading?: boolean
}

export type AssociatedUsersRelationsKeys =
  | 'executor'
  | 'interaction'
  | 'organization_auditor'
  | 'organization_executor'
  | 'organization_owner'
  | 'organization_planned_auditor'
  | 'organization_planned_executor'
  | 'organization_planned_owner'
  | 'organization_planned_requester'
  | 'organization_planned_sponsor'
  | 'organization_planned_stakeholder'
  | 'organization_requester'
  | 'organization_required_auditor'
  | 'organization_required_executor'
  | 'organization_required_owner'
  | 'organization_required_requester'
  | 'organization_required_sponsor'
  | 'organization_required_stakeholder'
  | 'organization_sponsor'
  | 'organization_stakeholder'
  | 'planned_team_auditor'
  | 'planned_team_executor'
  | 'planned_team_owner'
  | 'planned_team_requester'
  | 'planned_team_sponsor'
  | 'planned_team_stakeholder'
  | 'required_team_auditor'
  | 'required_team_executor'
  | 'required_team_owner'
  | 'required_team_requester'
  | 'required_team_sponsor'
  | 'required_team_stakeholder'
  | 'team_auditor'
  | 'team_executor'
  | 'team_owner'
  | 'team_requester'
  | 'team_sponsor'
  | 'team_stakeholder'

export type AssociatedUsersRelationsProps = {
  relation: AssociatedUsersRelationsKeys
  readableName: string
  image_path?: string
}[]

type SectionProps = {
  columns: ColumnProps[]
}

export type LayoutProps = {
  column_labels_text_fading?: boolean
  order_by?: SortOption[]
  sections?: SectionProps[]
}

export type KanbanCardsProps = {
  props: {
    uuid_objectclass: string
    uuid_statusaction_classification?: string
    card_model: CardModel
    group_by: string
    return_on_finished_interaction?: boolean
    layout?: LayoutProps
    styleMap?: StyleMap
    objects?: YesObject[]
    associated_users_relations?: AssociatedUsersRelationsProps
    visualizeUrl?: string
    polling_interval?: number
    task_card_configuration?: AMFTaskCardConfiguration
    orders_to_use?: string[]
  }
}

export const KanbanTypeGuard = Record({
  uuid_objectclass: String,
  uuid_filteraction: Optional(String),
  uuid_statusaction_classification: Optional(String),
  card_model: CardModelGuard,
  group_by: String,
  return_on_finished_interaction: Optional(Boolean),
  layout: Optional(LayoutTypeGuard),
  styleMap: Optional(Unknown),
  polling_interval: Optional(Number),
  objects: Optional(Array(Unknown)),
  associated_users_relations: Optional(Array(AssociatedUsersRelationTypeGuard)),
  visualizeUrl: Optional(String),
  orders_to_use: Optional(Array(String)),
  task_card_configuration: Optional(Unknown)
})
